import { Tooltip, TooltipContent, TooltipTrigger } from '@design-system';
import { CareEnvironment } from '@shared/appModel';
import { DAYS_IN_MONTH } from '@shared/calculations/funding/projection/sharedConstants';
import { currency } from '../formatters';
import { HeaderTooltip } from '../HeaderTooltip';
import { AccumulationWhenCell, GrowthValueCell } from '../../accumulationTimeline/cells';
import type { AccumulationColumnSpec, AccumulationCtx } from '../../accumulationTimeline/types';
import {
  singlePoolPayoutCellTooltip,
  traditionalLtcPresentation,
} from './traditionalLtcPresentation';
import type { StrategyPresentation } from './types';

// ManhattanLife OmniFlex™ Short-Term Care — dedicated day-denominated carrier
// product (manhattanLifeStcStrategy). There is NO dollar pool and NO death
// benefit: the base pool is the unlimited sentinel, and rather than render a
// misleading "Unlimited" the pool column is repurposed (via the `poolCell`
// override) to show the product's real "pool" — PER-TRACK day counters. The
// facility base policy and the Home Health Care rider each carry their OWN
// benefit period (restorable) and lifetime maximum (2× the period, never
// restores; the policy is terminal only when every elected track's lifetime
// is spent). The cell headlines the counter of the row's care setting, with
// the other track summarized beneath. The daily cap follows the carrier's
// Accrued Daily Benefit curve, not a generic inflation rate, and the Fast-50™
// election pays 50% of the accrued benefit with no elimination period. Every
// value below is READ off the precomputed month state/deltas/facts, terms,
// and year snapshots — the presentation never re-derives strategy math
// (new-strategy-prompt constraint #9).

// Presentation-local shapes for the strategy slots (the presentation owns its
// own casts — see StrategyPresentation.paidCellExtras).
interface ManhattanLifeStcLikeState {
  facilityBenefitPeriodDaysRemaining?: number;
  facilityLifetimeDaysRemaining?: number;
  homeBenefitPeriodDaysRemaining?: number;
  homeLifetimeDaysRemaining?: number;
  benefitFreeDayStreak?: number;
  indemnitySurplus?: number;
}

interface ManhattanLifeStcLikeDeltas {
  facilityBenefitPeriodDaysConsumed?: number;
  homeBenefitPeriodDaysConsumed?: number;
  facilityRestorationDaysRestored?: number;
  homeRestorationDaysRestored?: number;
  fast50DaysPaid?: number;
  indemnitySurplusAccrued?: number;
  indemnitySurplusApplied?: number;
}

// The HHC rider (and with it the home day-counter track) exists iff the home
// setting carries a positive elected benefit — the same signal the strategy
// uses. State can't answer this after the home lifetime hits 0.
function hasHomeTrack(terms: ClaimTerms): boolean {
  return (terms.nominalDailyBenefitByEnvironment?.[CareEnvironment.home] ?? 0) > 0;
}

// The track whose counters the row's care setting draws from (mirrors the
// strategy's funding-track rule): home iff the row is home care and the HHC
// rider exists; every other setting — including Fast-50's riderless-home
// facility fallback — reads the facility track.
function isHomeTrackRow(month: MonthlyEntry, terms: ClaimTerms): boolean {
  return month.context.careEnvironment === CareEnvironment.home && hasHomeTrack(terms);
}

// Restoration trigger threshold, surfaced in the streak line of the pool
// tooltip. Display constant only — the engine owns the actual trigger.
const RESTORATION_STREAK_DAYS = 180;

// Gate-eligible days the DAY COUNTERS stranded this row. Engine day
// accounting (manhattanLifeStcStrategy): payableDays = min(eligibleDays,
// funding track's period remaining, funding track's lifetime remaining), and
// `facts.coverageEligibleDays` carries the counter-CLAMPED payable count —
// so the stranded days are the gate-eligible days minus that clamped count
// (same convention as shortTermCarePresentation). Uncovered-environment rows
// (home without the HHC rider under standard indemnity, independent living)
// have a $0 applied cap and consume no counter days — their unpaid days are
// "not covered here", not counter-stranded, so they're excluded via the cap
// gate. `prePolicyDays` is subtracted so a pre-policy row doesn't read as
// stranded either.
function dayCounterStrandedDays(month: MonthlyEntry): number {
  // Care-free months (the strategy's hasCareNeed gate: dailyCareCost > 0)
  // publish every day-fact as 0 while the benefit cap stays positive, so
  // without this gate the whole month would read as 30 stranded days and the
  // tooltip would claim "benefit period exhausted" on a month with no care.
  if (month.facts.dailyCareCost <= 0) return 0;
  if ((month.facts.appliedMonthlyBenefitCap ?? 0) <= 0) return 0;
  const wpDays = Math.max(
    month.facts.waitingPeriodDaysConsumedHome,
    month.facts.waitingPeriodDaysConsumedFacility,
  );
  const gateEligibleDays =
    DAYS_IN_MONTH -
    month.facts.non2AdlDays -
    wpDays -
    month.events.ineligibleDayBreakdown.prePolicyDays;
  return Math.max(gateEligibleDays - month.facts.coverageEligibleDays, 0);
}

// Hover content for the day-counter pool cell: per-track counter mechanics,
// this row's day flow, and the care-free streak toward the 180-day
// Restoration trigger.
function dayCountersTooltip(month: MonthlyEntry, terms: ClaimTerms) {
  const close = month.state.strategyState as ManhattanLifeStcLikeState | undefined;
  const sd = month.deltas.strategyDeltas as ManhattanLifeStcLikeDeltas | undefined;
  if (!close) return null;

  const homeTrack = hasHomeTrack(terms);
  const streak = close.benefitFreeDayStreak ?? 0;
  const trackRows = (
    label: string,
    periodRemaining: number,
    lifetimeRemaining: number,
    daysConsumed: number,
    daysRestored: number,
  ) => (
    <>
      <span className="text-gray-300">{label} period remaining</span>
      <span className="font-semibold tabular-nums">{periodRemaining}d</span>
      {daysConsumed > 0 && (
        <>
          <span className="pl-2 text-gray-400">· used this month</span>
          <span className="tabular-nums text-rose-300">−{daysConsumed}d</span>
        </>
      )}
      {daysRestored > 0 && (
        <>
          <span className="pl-2 text-gray-400">· restored this month</span>
          <span className="tabular-nums text-emerald-300">+{daysRestored}d</span>
        </>
      )}
      <span className="text-gray-300">{label} lifetime remaining</span>
      <span className="font-semibold tabular-nums">
        {lifetimeRemaining}d{lifetimeRemaining <= 0 ? ' · exhausted' : ''}
      </span>
    </>
  );

  return (
    <TooltipContent className="max-w-xs text-xs">
      <div className="mb-1 font-semibold">Benefit period — day counters</div>
      <div className="mb-1 text-[10px] text-gray-300">
        OmniFlex is day-denominated: there is no dollar pool.
        {homeTrack
          ? ' The facility base policy and the Home Health Care rider each have their OWN benefit period and lifetime maximum — facility care and home care draw down separate counters and cannot borrow from each other.'
          : ''}{' '}
        Paid days draw a track&apos;s period and lifetime down together; a benefit period can
        restore after 180 consecutive care-free, benefit-free days, bounded by its lifetime
        counter. Lifetime exhaustion is permanent.
      </div>
      <div className="grid grid-cols-[auto_auto] gap-x-3 gap-y-0.5">
        {trackRows(
          homeTrack ? 'Facility' : 'Benefit',
          close.facilityBenefitPeriodDaysRemaining ?? 0,
          close.facilityLifetimeDaysRemaining ?? 0,
          sd?.facilityBenefitPeriodDaysConsumed ?? 0,
          sd?.facilityRestorationDaysRestored ?? 0,
        )}
        {homeTrack &&
          trackRows(
            'Home care',
            close.homeBenefitPeriodDaysRemaining ?? 0,
            close.homeLifetimeDaysRemaining ?? 0,
            sd?.homeBenefitPeriodDaysConsumed ?? 0,
            sd?.homeRestorationDaysRestored ?? 0,
          )}
        {streak > 0 && (
          <>
            <span className="text-gray-300">Care-free streak</span>
            <span className="font-semibold tabular-nums">
              {streak}d / {RESTORATION_STREAK_DAYS}d to restore
            </span>
          </>
        )}
      </div>
    </TooltipContent>
  );
}

// Pre-care "Policy Growth" columns. The universal default's monthly-benefit
// header claims the cap "starts at the at-issue monthly cap and grows on
// anniversaries when benefit inflation applies" and derives its formula from
// `terms.inflationRate` (deliberately 0 here) — both wrong for the Accrued
// Daily Benefit curve, which STARTS at 50% of elected and ramps up. The
// values themselves come straight off the year snapshots; only the
// explanation is product-specific.
function buildManhattanLifeStcAccumulationColumns({
  terms,
  timeline,
}: AccumulationCtx): AccumulationColumnSpec[] {
  const electedDaily = terms.nominalDailyBenefitBase;
  const columns: AccumulationColumnSpec[] = [
    {
      key: 'when',
      label: 'Policy year',
      sticky: true,
      render: row => <AccumulationWhenCell row={row} />,
    },
  ];

  if (timeline.some(r => r.snapshot.monthlyBenefitCap > 0.5)) {
    const hasRider = (terms.inflationCapYears ?? 0) > 0;
    columns.push({
      key: 'monthly-benefit',
      label: 'Monthly benefit',
      tooltip: (
        <HeaderTooltip
          description="The Accrued Daily Benefit × 30 for each policy year before care begins. OmniFlex starts at 50% of the elected daily benefit and steps up each policy anniversary — a claim in an early policy year pays materially less than the elected amount."
          formulas={[
            {
              label: 'Ramp (all policies)',
              expression:
                'elected daily × (50% + 10% per anniversary), reaching 100% at anniversary 5',
            },
            ...(hasRider
              ? [
                  {
                    label: 'Simple Inflation rider',
                    expression:
                      'then +5% of elected per anniversary through 25, capped at 2× elected',
                  },
                ]
              : []),
          ]}
          notes={
            hasRider
              ? 'Fixed-dollar steps anchored at the elected amount — not compound growth.'
              : 'Without the Simple Inflation rider the benefit stays at 100% of elected after anniversary 5.'
          }
        />
      ),
      render: (row, prev) => {
        const accruedDaily = row.snapshot.effectiveDailyBenefit;
        const accruedPct = electedDaily > 0 ? Math.round((accruedDaily / electedDaily) * 100) : 0;
        return (
          <GrowthValueCell
            value={row.snapshot.monthlyBenefitCap}
            prevValue={prev?.snapshot.monthlyBenefitCap ?? null}
            tooltip={
              <TooltipContent className="max-w-xs text-xs">
                <div className="mb-1 font-semibold">
                  Policy year {Math.floor(row.policyYear)} · accrued benefit
                </div>
                <div className="text-[10px] text-gray-300">
                  {currency(accruedDaily)}/day — {accruedPct}% of the elected{' '}
                  {currency(electedDaily)}/day. The Accrued Daily Benefit starts at 50% of the
                  elected amount at issue and steps up on each policy anniversary
                  {hasRider
                    ? ', continuing past 100% under the Simple Inflation rider (to at most 2× elected at anniversary 25).'
                    : ' until it reaches 100% at the 5th anniversary.'}
                </div>
              </TooltipContent>
            }
          />
        );
      },
    });
  }

  return columns;
}

export const manhattanLifeStcPresentation: StrategyPresentation = {
  ...traditionalLtcPresentation,

  accumulationColumns: buildManhattanLifeStcAccumulationColumns,

  // "Benefit cap" cell — headline the engine's applied monthly cap (accrued
  // daily benefit × 30, halved under Fast-50; $0 for uncovered environments).
  // The traditional presentation's cell is NOT reused because its tooltip
  // recomputes base × (1+r)^n from `terms.inflationRate`, which cannot match
  // the OmniFlex Accrued Daily Benefit curve (50% ramp + fixed-dollar steps +
  // 2× cap). We show the precomputed cap and describe the curve in words
  // instead of re-deriving it.
  benefitAmountCell: ({ month, terms }) => {
    if (month.events.ineligibilityReason === 'pre-policy') return null;
    const monthlyCap = month.facts.appliedMonthlyBenefitCap;
    const intYear = Math.floor(month.context.policyYear);
    // Rider signal: the strategy publishes `inflationRate: 0` (so generic
    // growth-factor consumers can't compute a wrong 1.5× from the 5%-simple
    // descriptors) and flags the Simple Inflation election via
    // `inflationCapYears` > 0 instead.
    const hasRider = (terms.inflationCapYears ?? 0) > 0;
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="cursor-help leading-tight">
            <div className="font-medium text-gray-900">{currency(monthlyCap)}</div>
            <div className="text-[10px] text-gray-500">
              {currency(monthlyCap / DAYS_IN_MONTH)}/day
            </div>
          </div>
        </TooltipTrigger>
        <TooltipContent className="max-w-xs text-xs">
          <div className="mb-1 font-semibold">
            Policy year {intYear} · monthly maximum
          </div>
          <div className="text-[10px] text-gray-300">
            {hasRider
              ? 'Accrued Daily Benefit × 30 for this row\'s care setting. The benefit starts at 50% of the elected daily amount and steps +10% of elected at each anniversary to 100% at anniversary 5; with the Simple Inflation rider it continues +5% of elected per anniversary through 25 — capped at 2× the elected amount.'
              : 'Accrued Daily Benefit × 30 for this row\'s care setting. The benefit starts at 50% of the elected daily amount and steps +10% of elected at each anniversary to 100% at anniversary 5, where it stays without the Simple Inflation rider.'}{' '}
            Under a Fast-50 election the cap is 50% of the accrued daily benefit. $0 means this
            care setting is not covered (home without the Home Health Care rider, independent
            living).
          </div>
        </TooltipContent>
      </Tooltip>
    );
  },

  benefitAmountValueForMonth: ({ month }) =>
    month.events.ineligibilityReason === 'pre-policy'
      ? undefined
      : month.facts.appliedMonthlyBenefitCap,

  // Pool column, repurposed: the dollar pool is the unlimited sentinel and
  // rendering "Unlimited" is misleading — what actually exhausts this policy
  // is the per-track benefit-period day counters. The full-cell override
  // headlines the counter of the row's care setting (facility for NH/ALF and
  // for riderless-home Fast-50 fallback rows; home for rider-funded home
  // rows), with that track's day flow and lifetime beneath and — on two-track
  // policies — a one-line summary of the other track. The tooltip carries the
  // full per-track mechanics and the care-free streak toward the 180-day
  // Restoration trigger.
  poolCell: ({ month, terms }) => {
    const close = month.state.strategyState as ManhattanLifeStcLikeState | undefined;
    if (!close) return <div className="text-gray-300">—</div>;
    if (month.events.ineligibilityReason === 'pre-policy') {
      return <div className="text-gray-300">—</div>;
    }

    const sd = month.deltas.strategyDeltas as ManhattanLifeStcLikeDeltas | undefined;
    const homeTrack = hasHomeTrack(terms);
    const homeRow = isHomeTrackRow(month, terms);
    const periodRemaining =
      (homeRow ? close.homeBenefitPeriodDaysRemaining : close.facilityBenefitPeriodDaysRemaining) ??
      0;
    const lifetimeRemaining =
      (homeRow ? close.homeLifetimeDaysRemaining : close.facilityLifetimeDaysRemaining) ?? 0;
    const daysConsumed =
      (homeRow ? sd?.homeBenefitPeriodDaysConsumed : sd?.facilityBenefitPeriodDaysConsumed) ?? 0;
    const daysRestored =
      (homeRow ? sd?.homeRestorationDaysRestored : sd?.facilityRestorationDaysRestored) ?? 0;
    const otherPeriodRemaining =
      (homeRow ? close.facilityBenefitPeriodDaysRemaining : close.homeBenefitPeriodDaysRemaining) ??
      0;

    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="cursor-help leading-tight">
            <div className="font-medium tabular-nums text-gray-900">
              {periodRemaining}d
              {homeTrack && (
                <span className="pl-1 text-[10px] font-normal text-gray-500">
                  {homeRow ? 'home' : 'facility'}
                </span>
              )}
            </div>
            {daysRestored > 0 && (
              <div className="text-[10px] tabular-nums text-emerald-700">
                +{daysRestored}d restored
              </div>
            )}
            {daysConsumed > 0 && (
              <div className="text-[10px] tabular-nums text-gray-500">−{daysConsumed}d</div>
            )}
            <div
              className={`text-[10px] tabular-nums ${
                lifetimeRemaining <= 0 ? 'text-rose-600' : 'text-gray-500'
              }`}
            >
              {lifetimeRemaining <= 0 ? 'lifetime exhausted' : `lifetime ${lifetimeRemaining}d`}
            </div>
            {homeTrack && (
              <div className="text-[10px] tabular-nums text-gray-400">
                {homeRow ? 'facility' : 'home'} {otherPeriodRemaining}d
              </div>
            )}
          </div>
        </TooltipTrigger>
        {dayCountersTooltip(month, terms)}
      </Tooltip>
    );
  },

  poolCellBlankKey: month => {
    const close = month.state.strategyState as ManhattanLifeStcLikeState | undefined;
    if (!close) return null;
    return [
      close.facilityBenefitPeriodDaysRemaining ?? 0,
      close.facilityLifetimeDaysRemaining ?? 0,
      close.homeBenefitPeriodDaysRemaining ?? 0,
      close.homeLifetimeDaysRemaining ?? 0,
    ].join('|');
  },

  // Total row: the end-of-projection counters per track (OmniFlex is
  // single-life, so there is no combined-spouses variant).
  poolAggregateCell: ({ closeStateAtEnd, terms }) => {
    const close = closeStateAtEnd.strategyState as ManhattanLifeStcLikeState | undefined;
    if (!close) return null;
    const homeTrack = hasHomeTrack(terms);
    const trackLine = (label: string | null, periodRemaining: number, lifetimeRemaining: number) => (
      <div className="leading-tight">
        <div className="font-medium tabular-nums text-gray-900">
          {periodRemaining}d
          {label && <span className="pl-1 text-[10px] font-normal text-gray-500">{label}</span>}
        </div>
        <div
          className={`text-[10px] tabular-nums ${
            lifetimeRemaining <= 0 ? 'text-rose-600' : 'text-gray-500'
          }`}
        >
          {lifetimeRemaining <= 0 ? 'lifetime exhausted' : `lifetime ${lifetimeRemaining}d`}
        </div>
      </div>
    );
    return (
      <div className="space-y-0.5">
        {trackLine(
          homeTrack ? 'facility' : null,
          close.facilityBenefitPeriodDaysRemaining ?? 0,
          close.facilityLifetimeDaysRemaining ?? 0,
        )}
        {homeTrack &&
          trackLine(
            'home',
            close.homeBenefitPeriodDaysRemaining ?? 0,
            close.homeLifetimeDaysRemaining ?? 0,
          )}
      </div>
    );
  },

  columnLabels: { pool: 'Benefit period left' },

  // "Projected payout" cell — the shared single-pool indemnity waterfall, with
  // the day-counter clamp surfaced the same way STCI surfaces its benefit
  // period ("Nd left in benefit period" / "benefit period exhausted" on the
  // cash line). The shared tooltip's default "drawn from the LTC pool" intro
  // and "Drawn from LTC pool $X" footer are false here (no dollar pool;
  // deltas.base.depletion is pinned at 0), so both are overridden.
  payoutCellTooltip: ({ month, terms }) =>
    singlePoolPayoutCellTooltip({
      month,
      terms,
      benefitPeriodStrandedDays: dayCounterStrandedDays(month),
      indemnityModeText:
        'Pays the accrued daily benefit as cash for each payable day regardless of actual cost — there is no dollar pool; paid days draw down the care setting track\'s day counters. Cash above the eligible cost banks as a surplus reserve, drained to cover cost on later shortfall months.',
      hidePoolDrawnFooter: true,
    }),

  // Sub-rows under the headline payout: Fast-50 payments, restoration events,
  // and drained surplus (sibling convention — see cmTwoPresentation).
  paidCellExtras: month => {
    const sd = month.deltas.strategyDeltas as ManhattanLifeStcLikeDeltas | undefined;
    const fast50Days = sd?.fast50DaysPaid ?? 0;
    const daysRestored =
      (sd?.facilityRestorationDaysRestored ?? 0) + (sd?.homeRestorationDaysRestored ?? 0);
    const surplusApplied = sd?.indemnitySurplusApplied ?? 0;
    const covered = month.deltas.payout.ltcCoveragePaid;
    const withSurplus = month.deltas.payout.ltcPayoutToCost;

    if (fast50Days === 0 && daysRestored === 0 && surplusApplied === 0) return null;

    return (
      <>
        {fast50Days > 0 && (
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="inline-block cursor-help border-b border-dotted border-sky-300 text-[10px] text-sky-700">
                Fast-50 · {fast50Days}d
              </div>
            </TooltipTrigger>
            <TooltipContent className="max-w-xs text-xs">
              Fast-50™ election: pays 50% of the accrued daily benefit as cash for care-need
              days in any covered setting (not independent living) — no elimination period, no
              licensed-care requirement. These
              days draw down the paying track&apos;s benefit-period and lifetime counters but do
              NOT satisfy the elimination period.
            </TooltipContent>
          </Tooltip>
        )}
        {daysRestored > 0 && (
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="inline-block cursor-help border-b border-dotted border-emerald-300 text-[10px] text-emerald-700">
                +{daysRestored}d period restored
              </div>
            </TooltipTrigger>
            <TooltipContent className="max-w-xs text-xs">
              Restoration of Benefits: after 180 consecutive days with no care needed and no
              benefit paid, each benefit period below its original length restores toward it —
              bounded by that track&apos;s remaining lifetime maximum (2× the benefit period,
              which never restores).
            </TooltipContent>
          </Tooltip>
        )}
        {surplusApplied > 0 && withSurplus !== covered && (
          <div className="inline-block text-[10px] text-emerald-700">
            +{currency(surplusApplied)} surplus
          </div>
        )}
      </>
    );
  },

  // No death benefit; the sentinel "pool limit" is flat and adds no
  // information; "Drawn to date" reads deltas.base.depletion, which this
  // strategy pins at 0 (the sentinel pool never depletes), so the column
  // would show a misleading $0 — payouts surface via the payout column and
  // the day counters instead.
  hiddenColumns: ['pool-limit', 'pool-drawn', 'death-benefit'],

  copy: {
    ...traditionalLtcPresentation.copy,
    poolTooltip: (
      <HeaderTooltip
        description="Days left in the benefit period of this row's care setting — the balance that actually exhausts this policy. OmniFlex is day-denominated: there is no lifetime dollar maximum. The facility base policy and the Home Health Care rider each carry their OWN benefit period and lifetime counters — facility and home care drain independently and cannot borrow from each other."
        formulas={[
          { label: 'Benefit period (per track)', expression: 'benefit period months × 30 days; restores after 180 consecutive care-free, benefit-free days' },
          { label: 'Lifetime maximum (per track)', expression: '2 × that track’s benefit period days; never restores — the policy is spent only when every elected track’s lifetime is exhausted' },
        ]}
        notes="A track's period and lifetime decrement together 1:1 on paid days (including Fast-50 days). Riderless-home Fast-50 payments draw the facility track."
      />
    ),
    payoutColumnTooltip: (
      <HeaderTooltip
        description="Cash indemnity paid this month, with cumulative dollars paid by the policy through this month in parentheses. OmniFlex pays the accrued daily benefit per eligible day regardless of billed charges; payable days are clamped by the day counters of the care setting's track (facility base policy vs. Home Health Care rider)."
        formulas={[
          {
            label: 'Payable days',
            expression:
              "min(eligible days, track's benefit-period days remaining, track's lifetime days remaining)",
          },
          { label: 'Standard indemnity', expression: 'accrued daily benefit × payable days' },
          {
            label: 'Fast-50 election',
            expression: '50% × accrued daily benefit × payable days (no elimination period)',
          },
        ]}
        notes="Eligible days exclude days the insured fails the 2-ADL trigger and (standard election only) elimination-period days. Fast-50 skips the elimination period but its days never satisfy it. Home care without the Home Health Care rider pays $0 under the standard election."
      />
    ),
    benefitAmountTooltip: (
      <HeaderTooltip
        description="Monthly maximum for this policy year — the Accrued Daily Benefit × 30, halved under a Fast-50 election."
        formulas={[
          {
            label: 'Ramp (all policies)',
            expression: 'elected daily × (50% + 10%/anniversary), reaching 100% at anniversary 5',
          },
          {
            label: 'With inflation rider',
            expression: 'then +5% of elected per anniversary through 25, capped at 2× elected',
          },
        ]}
        notes="Fixed-dollar steps anchored at the elected amount — not compound growth on the remaining balance."
      />
    ),
  },
};
